import {inject, Injectable} from "@angular/core";
import {DOCUMENT} from "@angular/common";
import {Meta, Title} from "@angular/platform-browser";
import {toSignal} from "@angular/core/rxjs-interop";
import {select, Store} from "@ngrx/store";
import {selectTenantInfo} from "../../store/selectors";

@Injectable({
  providedIn: "root"
})
export class MetaTagsGeneratorService {

  title = inject(Title);
  meta = inject(Meta);
  private document = inject(DOCUMENT);
  private tenantInfo = toSignal(inject(Store).pipe(select(selectTenantInfo)));

  addTags(data: { title: string, description: string, image: string, url?: string }) {

    // never blank the title (or a title injected into the HTML by the server): fall back to the school name
    const title = data?.title?.trim() || this.tenantInfo()?.schoolName;

    if (title) {
      this.title.setTitle(title);
      this.upsert({name: "title", content: title});
      this.upsert({property: "og:title", content: title});
      this.upsert({name: "twitter:title", content: title});
    }

    // remove empty tags instead of writing content="", which also clears stale values from the previous route
    this.upsert({name: "description", content: data?.description});
    this.upsert({property: "og:description", content: data?.description});
    this.upsert({name: "twitter:description", content: data?.description});

    // same fallback as the tags the server puts in the index.html (rest-api seo-html.utils.ts)
    const image = data?.image || this.tenantInfo()?.socialMediaBarConfig?.defaultImageUrl;

    // name="image" is read by the social media share widget
    this.upsert({name: "image", content: image});
    this.upsert({property: "og:image", content: image});
    this.upsert({name: "twitter:image", content: image});
    this.meta.updateTag({name: "twitter:card", content: image ? "summary_large_image" : "summary"});

    this.upsert({property: "og:url", content: this.absoluteUrl(data?.url)});
    this.meta.updateTag({property: "og:type", content: "website"});

    // leftovers from earlier versions of this service, they are not valid meta names
    ['name="url"', 'name="type"'].forEach(selector => this.meta.removeTag(selector));

  }

  /**
   * The server puts the home page tags in the index.html of every URL, because CloudFront does not pass the path
   * (rest-api seo.controller.ts). Pages that do not set their own tags must not keep the home page description,
   * so the injected tags are removed at the start of the first navigation, before any page component writes its own.
   */
  removeServerTagsOutsideHomePage(url: string) {

    const path = url.split(/[?#]/)[0];

    if (path === "/" || path === "/home") {
      return;
    }

    const siteName = this.document.head.querySelector<HTMLMetaElement>('meta[property="og:site_name"][data-och-seo]')?.content;

    this.document.head.querySelectorAll("meta[data-och-seo]").forEach(tag => tag.remove());

    // the injected <title> is the home page title as well
    if (siteName) {
      this.title.setTitle(siteName);
    }
  }

  /**
   * Keeps a single <link rel="canonical"> pointing at the current page (without query parameters),
   * on the custom domain once it is fully configured, so that /home?srsltid=... and the subdomain copy
   * of the site are not indexed as separate pages.
   */
  updateCanonical() {

    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!this.tenantInfo()) {
      link?.remove();
      this.upsert({property: "og:url", content: null});
      return;
    }

    if (!link) {
      link = this.document.createElement("link");
      link.rel = "canonical";
      this.document.head.appendChild(link);
    }

    link.href = this.absoluteUrl(this.document.location.pathname);

    // og:url must follow the page too, not keep the value of the last page that called addTags()
    this.upsert({property: "og:url", content: link.href});
  }

  private absoluteUrl(path: string) {
    return path ? `${this.siteOrigin()}${path.startsWith("/") ? path : "/" + path}` : null;
  }

  private siteOrigin() {
    const tenant = this.tenantInfo();
    const customDomain = tenant?.customDomainConfig?.currentStep === "completed" ? tenant?.customDomain : null;
    return customDomain ? `https://${customDomain.replace(/^https?:\/\//, "").replace(/\/+$/, "")}` : this.document.location.origin;
  }

  private upsert(tag: { name?: string, property?: string, content: string }) {
    if (tag.content) {
      this.meta.updateTag(tag);
    } else {
      this.meta.removeTag(tag.name ? `name="${tag.name}"` : `property="${tag.property}"`);
    }
  }

}
